/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { DashboardTemplate } from '@app/core/types';
import { groupBy, partition } from 'lodash-es';

type Result<Value, Error> = { value: Value; error: null } | { value: null; error: Error };
type DashboardTemplateNameResult = { category: string; name: string } | { category: null; name: string };
export type DashboardTemplateNameError = 'empty category' | 'empty name' | 'empty name and category';
export const DASHBOARD_TEMPLATE_NAME_SEPARATOR = '/';

export const parseDashboardTemplateName = (
  name: string,
): Result<DashboardTemplateNameResult, DashboardTemplateNameError> => {
  if (!name.trim()) return { value: null, error: 'empty name' };
  const [rawLeft, ...right] = name.split(DASHBOARD_TEMPLATE_NAME_SEPARATOR);
  const rawRight = right.join(DASHBOARD_TEMPLATE_NAME_SEPARATOR);
  const parsedLeft = rawLeft?.trim() ?? '';
  const parsedRight = rawRight.trim();
  const hasSeparator = name.includes(DASHBOARD_TEMPLATE_NAME_SEPARATOR);
  if (parsedLeft && !parsedRight && !hasSeparator) return { error: null, value: { name: parsedLeft, category: null } };
  if (parsedLeft && !parsedRight && hasSeparator) return { value: null, error: 'empty name' };
  if (!parsedLeft && parsedRight) return { value: null, error: 'empty category' };
  if (!parsedLeft && !parsedRight) return { value: null, error: 'empty name and category' };
  else return { error: null, value: { name: parsedRight, category: parsedLeft } };
};

export const prettifyDashboardTemplateName = (templateName: string): string => {
  const parsedName = parseDashboardTemplateName(templateName);
  if (!parsedName.value) return templateName;
  const { category, name } = parsedName.value;
  return category ? [category, name].join(` ${DASHBOARD_TEMPLATE_NAME_SEPARATOR} `) : name;
};

const sortByName = <T extends { name: string }>(items: T[]) => [...items].sort((a, b) => a.name.localeCompare(b.name));

type GroupTree<T> = ({ type: 'item'; value: T } | { type: 'group'; name: string; items: T[] })[];

export const groupDashboardTemplatesByCategory = (
  dashboardTemplates: DashboardTemplate[],
): GroupTree<DashboardTemplate> => {
  const groupedObject = groupBy(dashboardTemplates, (dbt) => {
    const result = parseDashboardTemplateName(dbt.name);
    return result.value?.category ?? null;
  });
  const [noGroup, grouped] = partition(Object.entries(groupedObject), ([group, items]) => group === 'null');
  return [
    ...noGroup.flatMap(([name, items]) => sortByName(items).map((item) => ({ type: 'item' as const, value: item }))),
    ...sortByName(grouped.map(([name, items]) => ({ type: 'group' as const, name, items: sortByName(items) }))),
  ];
};
